
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { calculateDistance, formatDistance } from "@/utils/distance";
import { ActiveFiltersState } from "./useHomePageFilters";
import { isWithinInterval, parseISO, endOfDay, startOfDay, eachDayOfInterval } from "date-fns";

interface ChefData {
  id?: string;
  first_name?: string;
  last_name?: string;
  latitude?: number | null;
  longitude?: number | null;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
}

interface MealWithDistance {
  id: string;
  name: string;
  price: number;
  image: string;
  images: string[];
  description: string;
  rating: number;
  reviews_count: number;
  available_portions: number;
  delivery_available: boolean;
  delivery_fee: number;
  status: string;
  dietary_tags: string[];
  availability_slots: any[];
  meal_type: string;
  preparation_type: "scheduled" | "on_demand";
  prep_time_hours: number;
  available_days: string[];
  chef: ChefData;
  distance: string;
  rawDistance?: number;
}

export const useHomeMeals = (
  activeFilters: ActiveFiltersState,
  userLocation: { lat: number; lng: number } | null
) => {
  return useQuery({
    queryKey: ['meals', activeFilters, userLocation],
    queryFn: async () => {
      console.log('Fetching meals with filters:', activeFilters);

      // Start with base query including chef profile data - REMOVED location constraints
      let query = supabase
        .from('meals')
        .select(`
          id,
          name,
          price,
          image,
          images,
          description,
          rating,
          reviews_count,
          available_portions,
          delivery_available,
          delivery_fee,
          status,
          dietary_tags,
          availability_slots,
          meal_type,
          preparation_type,
          prep_time_hours,
          available_days,
          chef:profiles!meals_chef_id_fkey (
            id,
            first_name,
            last_name,
            latitude,
            longitude,
            address,
            city,
            state,
            postal_code
          )
        `)
        .eq('status', 'active');

      // Apply filters
      if (activeFilters.rating > 0) {
        query = query.gte('rating', activeFilters.rating);
      }

      if (activeFilters.filters?.length > 0) {
        query = query.contains('dietary_tags', activeFilters.filters);
      }

      if (activeFilters.query?.trim()) {
        query = query.ilike('name', `%${activeFilters.query.trim()}%`);
      }

      if (activeFilters.availablePortions > 1) {
        query = query.gte('available_portions', activeFilters.availablePortions);
      }

      if (activeFilters.deliveryOnly) {
        query = query.eq('delivery_available', true);
      }

      console.log('Executing meals query');
      const { data: mealsData, error } = await query;

      if (error) {
        console.error('Error fetching meals:', error);
        throw error;
      }

      // Filter meals based on date range
      const filteredMeals = (mealsData || []).filter(meal => {
        if (!activeFilters.dateRange?.from) return true;

        const searchStart = startOfDay(activeFilters.dateRange.from);
        const searchEnd = endOfDay(activeFilters.dateRange.to || activeFilters.dateRange.from);
        const daysInRange = eachDayOfInterval({ start: searchStart, end: searchEnd });

        // Handle on-demand meals
        if (meal.preparation_type === 'on_demand') {
          if (!meal.available_days || meal.available_days.length === 0) return true;
          
          return daysInRange.some(date => {
            const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
            return meal.available_days.includes(dayName);
          });
        }

        // Handle scheduled meals
        return meal.availability_slots.some((slot: any) => {
          try {
            if (slot.recurring) {
              const days = slot.recurring.days || [];
              const untilDate = parseISO(slot.recurring.until);
              
              return daysInRange.some(day => {
                if (day > untilDate) return false;
                const dayMap: { [key: string]: number } = {
                  'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
                  'Thursday': 4, 'Friday': 5, 'Saturday': 6
                };
                const availableDays = days.map((d: string) => dayMap[d]);
                return availableDays.includes(day.getDay());
              });
            }

            if (slot.dateRange) {
              const slotStart = startOfDay(parseISO(slot.dateRange.from));
              const slotEnd = endOfDay(parseISO(slot.dateRange.to || slot.dateRange.from));

              return (
                (searchStart <= slotEnd && searchEnd >= slotStart) ||
                (slotStart <= searchEnd && slotEnd >= searchStart)
              );
            }

            return false;
          } catch (error) {
            console.error('Error processing availability slot:', error);
            return false;
          }
        });
      });

      // Apply distance calculations if userLocation is available
      if (userLocation) {
        const withDistance: MealWithDistance[] = (filteredMeals || []).map(meal => {
          // Create a safer chef data object with default values
          const chef: ChefData = meal.chef || { first_name: '', last_name: '' };
          
          // Get chef latitude and longitude with proper type safety
          const chefLat = typeof chef.latitude === 'number' ? Number(chef.latitude) : null;
          const chefLng = typeof chef.longitude === 'number' ? Number(chef.longitude) : null;
          
          console.log(`Meal ${meal.name} chef data:`, {
            firstName: chef.first_name || 'Unknown', 
            lastName: chef.last_name || '',
            hasLocation: (chefLat !== null && chefLng !== null)
          });
          
          // Calculate distance only if we have both user location and chef location
          let distanceValue = null;
          if (chefLat !== null && chefLng !== null && !isNaN(chefLat) && !isNaN(chefLng)) {
            distanceValue = calculateDistance(
              userLocation.lat,
              userLocation.lng,
              chefLat,
              chefLng
            );
          }
          
          return {
            ...meal,
            chef: chef, // Ensure chef data is preserved
            distance: distanceValue !== null ? formatDistance(distanceValue) : "Distance unavailable",
            preparation_type: (meal.preparation_type === "on_demand" ? "on_demand" : "scheduled") as "on_demand" | "scheduled",
            prep_time_hours: meal.prep_time_hours,
            rawDistance: distanceValue
          } as MealWithDistance;
        });

        if (activeFilters.distance > 0) {
          return withDistance.filter(meal => {
            // Use the raw distance value for filtering if available
            if (typeof meal.rawDistance === 'number') {
              return meal.rawDistance <= activeFilters.distance;
            }
            
            // Fall back to string parsing if needed
            if (typeof meal.distance === 'string' && meal.distance.includes('mi')) {
              const distanceValue = parseFloat(meal.distance);
              return !isNaN(distanceValue) && distanceValue <= activeFilters.distance;
            }
            
            // Exclude meals with unavailable distance when filtering by distance
            return false;
          });
        }

        return withDistance;
      }

      // When no user location is available, return meals with appropriate chef data
      return (filteredMeals || []).map(meal => {
        // Create a proper fallback for chef data
        const chef = meal.chef || { first_name: '', last_name: '' };
        
        return {
          ...meal,
          chef: chef, // Ensure chef data is preserved
          distance: "Distance unavailable",
          preparation_type: (meal.preparation_type === "on_demand" ? "on_demand" : "scheduled") as "on_demand" | "scheduled",
          prep_time_hours: meal.prep_time_hours
        } as MealWithDistance;
      });
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    placeholderData: (old) => old,
    select: (data) => {
      console.log(`Processing ${data.length} meals`);
      return data;
    }
  });
};
